import React, { useState, useEffect, useCallback } from 'react';
import {
    Wallet,
    TrendingUp,
    Search,
    Loader2,
    Clock,
    CheckCircle2,
    Calendar,
    ArrowRight,
    Download,
    MoreHorizontal,
    Banknote,
    PieChart,
    ChevronDown,
    X,
    CreditCard,
    Info
} from 'lucide-react';
import { API_BASE_URL } from '../../config';

const ProviderTransactions = ({ authToken }) => {
    const [transactions, setTransactions] = useState([]);
    const [walletSummary, setWalletSummary] = useState({
        total_revenue: 0,
        platform_commission: 0,
        available_balance: 0,
        pending_withdrawal: 0,
        total_payouts: 0,
        commission_percentage: 15
    });
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('Transactions');
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [withdrawAmount, setWithdrawAmount] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [bankDetails, setBankDetails] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            // Fetch Transactions
            const txResponse = await fetch(`${API_BASE_URL}/admin/transactions`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (txResponse.ok) {
                const data = await txResponse.json();
                setTransactions(Array.isArray(data) ? data : []);
            }

            // Fetch Wallet Summary
            const summaryResponse = await fetch(`${API_BASE_URL}/provider/wallet/summary`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (summaryResponse.ok) {
                const data = await summaryResponse.json();
                setWalletSummary(data);
                setWithdrawAmount(String(data.available_balance || 0));
            }

            // Fetch Bank Details for modal
            const profileResponse = await fetch(`${API_BASE_URL}/admin/profile`, {
                headers: { Authorization: `Bearer ${authToken}` }
            });
            if (profileResponse.ok) {
                const data = await profileResponse.json();
                setBankDetails(data.bank_details);
            }
        } catch (err) {
            console.error('Failed to fetch transaction data:', err);
        } finally {
            setLoading(false);
        }
    }, [authToken]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleWithdraw = async () => {
        if (!withdrawAmount || Number(withdrawAmount) <= 0) return;
        if (Number(withdrawAmount) > walletSummary.available_balance) {
            alert('Amount exceeds available balance');
            return;
        }

        try {
            setSubmitting(true);
            const response = await fetch(`${API_BASE_URL}/provider/wallet/withdraw`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authToken}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    requested_amount: Number(withdrawAmount),
                    bank_details: bankDetails // Backend might already have them, but payload says to send
                })
            });

            if (response.ok) {
                alert('Withdrawal request submitted successfully!');
                setShowWithdrawModal(false);
                fetchData(); // Refresh data
            } else {
                const error = await response.json();
                alert(error.message || 'Failed to submit request');
            }
        } catch (err) {
            console.error('Withdrawal error:', err);
            alert('An error occurred. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount || 0);
    };

    const filteredTransactions = transactions.filter(txn =>
        (txn.orderId || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (txn.course || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-[#F8FAFC] p-4 md:p-8 space-y-8 font-sans">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-extrabold text-[#1E293B] tracking-tight">Wallet & Earnings Center</h1>
                    <p className="text-[#64748B] text-lg mt-1 font-medium">Track your earnings, wallet balance, and withdrawals in one place.</p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <SummaryCard
                    title="Total Revenue"
                    value={formatCurrency(walletSummary.total_revenue)}
                    subtitle="Gross course sales"
                    icon={<TrendingUp size={24} strokeWidth={2.5} />}
                    color="blue"
                />
                <SummaryCard
                    title="Platform Commission"
                    value={`${formatCurrency(walletSummary.platform_commission)} (${walletSummary.commission_percentage}%)`}
                    subtitle={`Commission at ${walletSummary.commission_percentage}%`}
                    icon={<PieChart size={24} strokeWidth={2.5} />}
                    color="purple"
                />
                <SummaryCard
                    title="Available Balance"
                    value={formatCurrency(walletSummary.available_balance)}
                    subtitle="Ready to withdraw"
                    icon={<Wallet size={24} strokeWidth={2.5} />}
                    color="emerald"
                    highlight
                />
                <SummaryCard
                    title="Pending Withdrawal"
                    value={formatCurrency(walletSummary.pending_withdrawal)}
                    subtitle="Awaiting approval"
                    icon={<Clock size={24} strokeWidth={2.5} />}
                    color="amber"
                />
            </div>

            <div className="relative bg-gradient-to-r from-[#F1F5F9] to-white rounded-[32px] p-8 border border-slate-200 shadow-sm overflow-hidden flex flex-col md:flex-row items-center gap-12">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/30 rounded-full blur-3xl -mr-32 -mt-32" />
                <div className="relative z-10 w-full md:w-1/3 flex justify-center">
                    <img
                        src="https://cdn-icons-png.flaticon.com/512/2489/2489756.png"
                        alt="Wallet Illustration"
                        className="w-64 h-auto drop-shadow-2xl animate-pulse-slow"
                    />
                </div>
                <div className="relative z-10 w-full md:w-2/3 space-y-6">
                    <div>
                        <h2 className="text-slate-500 font-bold text-2xl mb-2">Wallet Balance</h2>
                        <div className="text-6xl font-black text-[#0F172A] flex items-center gap-2">
                            {formatCurrency(walletSummary.available_balance)}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-4">
                        <button
                            onClick={() => setShowWithdrawModal(true)}
                            disabled={walletSummary.available_balance <= 0}
                            className="bg-[#2563EB] text-white px-8 py-4 rounded-2xl font-bold flex items-center gap-3 shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all hover:scale-105 active:scale-95 group disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                        >
                            Withdraw Funds
                            <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="space-y-6">
                <div className="flex border-b border-slate-200 gap-8">
                    {['Transactions', 'Withdraw History', 'Wallet Ledger'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`pb-4 px-2 text-sm font-bold transition-all relative ${activeTab === tab ? 'text-blue-600' : 'text-slate-400 hover:text-slate-500'}`}
                        >
                            {tab}
                            {activeTab === tab && (
                                <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-600 rounded-t-full shadow-[0_-2px_8px_rgba(37,99,235,0.4)]" />
                            )}
                        </button>
                    ))}
                </div>

                <div className="flex flex-col md:flex-row justify-between gap-4 items-center">
                    <div className="flex flex-wrap gap-3 w-full md:w-auto">
                        <div className="relative flex-grow md:flex-grow-0 min-w-[300px]">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                            <input
                                type="text"
                                placeholder="Search by Order ID or Course"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full bg-white border border-slate-200 rounded-2xl py-3 pl-12 pr-4 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                            />
                        </div>
                    </div>
                    <button className="flex items-center gap-2 text-slate-500 font-bold text-sm bg-white border border-slate-200 px-4 py-3 rounded-2xl hover:bg-slate-50 transition-all active:scale-95">
                        <Download size={18} /> Download CSV
                    </button>
                </div>

                <div className="bg-white border border-slate-100 rounded-[32px] overflow-hidden shadow-sm">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <Loader2 className="animate-spin text-blue-600" size={40} />
                            <p className="text-slate-400 font-bold">Fetching your transactions...</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-slate-50/50">
                                        <th className="px-8 py-5 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Date</th>
                                        <th className="px-8 py-5 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Order ID</th>
                                        <th className="px-8 py-5 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Course</th>
                                        <th className="px-8 py-5 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 text-right">Gross</th>
                                        <th className="px-8 py-5 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 text-right">Commision</th>
                                        <th className="px-8 py-5 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100 text-right">Net Earning</th>
                                        <th className="px-8 py-5 text-[11px] font-black text-slate-400 uppercase tracking-widest border-b border-slate-100">Status</th>
                                        <th className="px-8 py-5 text-right border-b border-slate-100"></th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-50">
                                    {filteredTransactions.length > 0 ? filteredTransactions.map((txn, idx) => (
                                        <tr key={idx} className="group hover:bg-blue-50/30 transition-colors">
                                            <td className="px-8 py-5 text-sm font-bold text-slate-500">{txn.date}</td>
                                            <td className="px-8 py-5 text-sm font-black text-slate-700">{txn.orderId}</td>
                                            <td className="px-8 py-5 text-sm font-bold text-slate-600">{txn.course}</td>
                                            <td className="px-8 py-5 text-sm font-black text-slate-900 text-right">{formatCurrency(txn.gross)}</td>
                                            <td className="px-8 py-5 text-sm font-bold text-slate-500 text-right">{formatCurrency(txn.commission)}</td>
                                            <td className="px-8 py-5 text-sm font-black text-slate-900 text-right">{formatCurrency(txn.net)}</td>
                                            <td className="px-8 py-5">
                                                <span className={`px-3 py-1.5 rounded-xl text-xs font-black ring-1 ${txn.status === 'Completed' ? 'bg-emerald-50 text-emerald-600 ring-emerald-100' : 'bg-amber-50 text-amber-600 ring-amber-100'}`}>
                                                    {txn.status}
                                                </span>
                                            </td>
                                            <td className="px-8 py-5 text-right">
                                                <button className="text-slate-300 hover:text-slate-600 transition-colors">
                                                    <MoreHorizontal size={20} />
                                                </button>
                                            </td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan="8" className="px-8 py-10 text-center text-slate-400 font-bold font-sans">No transactions found.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {showWithdrawModal && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white rounded-[32px] w-full max-w-lg shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="p-6 flex justify-between items-center border-b border-slate-100">
                            <h2 className="text-2xl font-black text-slate-800 tracking-tight">Withdraw Funds</h2>
                            <button
                                onClick={() => setShowWithdrawModal(false)}
                                className="w-10 h-10 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-all"
                            >
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-8 space-y-6">
                            <div className="flex justify-between items-center text-sm font-bold">
                                <span className="text-slate-400 tracking-wide uppercase">Available Balance:</span>
                                <span className="text-slate-800 font-black">{formatCurrency(walletSummary.available_balance)}</span>
                            </div>

                            <div className="relative group">
                                <input
                                    type="number"
                                    value={withdrawAmount}
                                    onChange={(e) => setWithdrawAmount(e.target.value)}
                                    placeholder="0"
                                    className="w-full bg-slate-50 border border-slate-200 rounded-3xl py-6 px-8 text-3xl font-black text-slate-900 focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                                <label className="absolute left-8 -top-3 px-2 bg-white text-[10px] font-black text-blue-600 tracking-widest uppercase">Enter Amount (₹)</label>
                            </div>

                            <div className="text-sm font-bold text-slate-400">
                                Remaining Balance: <span className="text-slate-800">{formatCurrency(walletSummary.available_balance - (Number(withdrawAmount) || 0))}</span>
                            </div>

                            {bankDetails ? (
                                <div className="bg-slate-50/50 border border-slate-100 rounded-3xl p-6 space-y-3">
                                    <h4 className="text-[10px] font-black text-blue-600 uppercase tracking-widest">Bank Destination</h4>
                                    <div className="flex justify-between items-center text-xs font-bold">
                                        <span className="text-slate-400 uppercase">Bank:</span>
                                        <span className="text-slate-600 font-black">{bankDetails.bank_name}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs font-bold">
                                        <span className="text-slate-400 uppercase">IFSC Code:</span>
                                        <span className="text-slate-600 font-black uppercase text-indigo-500 font-mono tracking-widest">{bankDetails.ifsc_code}</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs font-bold">
                                        <span className="text-slate-400 uppercase">Account:</span>
                                        <span className="text-slate-600 font-black">•••• {bankDetails.account_number?.slice(-4)}</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex gap-3 text-amber-700">
                                    <Info size={20} className="shrink-0" />
                                    <p className="text-xs font-bold leading-relaxed">Please complete your bank details in profile settings before making a withdrawal.</p>
                                </div>
                            )}

                            <div className="flex items-center justify-between gap-6 pt-4">
                                <button
                                    className={`flex-grow bg-[#2563EB] text-white py-5 rounded-2xl font-black text-lg shadow-xl shadow-blue-200 hover:bg-blue-700 transition-all active:scale-95 flex items-center justify-center gap-2 ${submitting ? 'opacity-70 cursor-wait' : ''}`}
                                    onClick={handleWithdraw}
                                    disabled={submitting || !bankDetails || !withdrawAmount || Number(withdrawAmount) <= 0}
                                >
                                    {submitting ? <Loader2 size={24} className="animate-spin" /> : 'Submit Request'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                @keyframes pulse-slow {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }
                .animate-pulse-slow {
                    animation: pulse-slow 4s ease-in-out infinite;
                }
            `}</style>
        </div>
    );
};

const SummaryCard = ({ title, value, subtitle, icon, color, highlight }) => {
    const colors = {
        blue: 'bg-blue-50/50 text-blue-600 hover:shadow-blue-50',
        purple: 'bg-purple-50/50 text-purple-600 hover:shadow-purple-50',
        emerald: 'bg-emerald-50/50 text-emerald-600 hover:shadow-emerald-50',
        amber: 'bg-amber-50/50 text-amber-600 hover:shadow-amber-50',
        indigo: 'bg-indigo-50/50 text-indigo-600 hover:shadow-indigo-50'
    };

    return (
        <div className={`bg-white rounded-3xl p-6 border border-slate-100 shadow-sm transition-all hover:shadow-md group relative overflow-hidden ${highlight ? 'ring-2 ring-emerald-500/20' : ''}`}>
            <div className={`absolute top-0 right-0 w-20 h-20 ${colors[color].split(' ')[0]} rounded-bl-full -mr-4 -mt-4 transition-transform group-hover:scale-110`} />
            <div className="relative flex flex-col gap-4">
                <div className={`w-12 h-12 ${colors[color].split(' ')[0]} rounded-2xl flex items-center justify-center ${colors[color].split(' ')[1]}`}>
                    {icon}
                </div>
                <div>
                    <h3 className="text-slate-500 font-bold text-sm tracking-wide mb-1">{title}</h3>
                    <div className="text-2xl font-black text-slate-900">{value}</div>
                    <p className={`text-[10px] ${highlight ? colors[color].split(' ')[1] : 'text-slate-400'} font-bold mt-1 tracking-widest uppercase`}>{subtitle}</p>
                </div>
            </div>
        </div>
    );
};

export default ProviderTransactions;
